import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { ACTION_LIMITS } from '../ApiAction/useHttpRequestActionFormV2.hook';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import {
  ConnectRemoteMcpDto,
  RemoteMcpCreateDto,
  RemoteMcpDto,
  RemoteMcpTool,
  RemoteMcpToolNames,
} from '@/Domain/api/Action/dto/RemoteMCP.dto';

import { useModalStore } from '@/Domain/store/modal.store';

const isSubset = (setA: Set<any>, setB: Set<any>) => {
  let result = true;
  setA.forEach((val) => {
    if (!setB.has(val)) {
      result = false;
    }
  });
  return result;
};

const getDefaultTools = (toolNames: string[]): RemoteMcpTool[] =>
  toolNames.map((toolName) => ({
    name: toolName,
    is_active: true,
  }));

const updateToolNamesOnReconnect = (
  toolNames: string[],
  remoteMcpAction: RemoteMcpDto,
): RemoteMcpTool[] =>
  toolNames.map((toolName) => {
    const tool = remoteMcpAction.allowed_tools.find((tool) => tool.name === toolName);
    return {
      name: toolName,
      is_active: tool ? tool.is_active : remoteMcpAction.default_tool_is_active,
    };
  });

const connectRemoteMcpActionSchema = z.object({
  remote_mcp_url: z.string().url().min(1),
  headers: z
    .array(
      z.object({
        id: z.number().or(z.string()),
        name: z.string().max(ACTION_LIMITS.BODY_KEY, {
          message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
            limit: ACTION_LIMITS.BODY_KEY,
          }),
        }),
        value: z.string().max(ACTION_LIMITS.BODY_VALUE, {
          message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
            limit: ACTION_LIMITS.BODY_VALUE,
          }),
        }),
      }),
    )
    .refine((items) => {
      const names = items.map((item) => item.name.trim());
      return new Set(names).size === names.length;
    }),
});

export type ConnectRemoteMcpForm = z.infer<typeof connectRemoteMcpActionSchema>;

export const useConnectRemoteMcpActionForm = (remoteMcpAction?: RemoteMcpDto) => {
  const { t } = useTranslation('remote_mcp');

  const { setParams } = useModalStore();
  const form = useForm<ConnectRemoteMcpForm>({
    mode: 'onChange',
    defaultValues: {
      remote_mcp_url: remoteMcpAction?.remote_mcp_url || '',
      headers: remoteMcpAction?.headers || [],
    },
    resolver: zodResolver(connectRemoteMcpActionSchema),
  });

  const connectRemoteMcpMutation = useMutation<RemoteMcpToolNames, unknown, ConnectRemoteMcpDto>({
    mutationKey: [QueryKey.actions.remoteMcp.connect],
  });
  const createRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpCreateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.create],
  });

  const defaultPatchRemoteMcpMutationOptions = queryClient.getMutationDefaults([
    QueryKey.actions.remoteMcp.update,
  ]);
  const patchRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, Partial<RemoteMcpDto>>({
    mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    ...defaultPatchRemoteMcpMutationOptions,
    onSuccess: (data, variables, context) => {
      defaultPatchRemoteMcpMutationOptions?.onSuccess?.(data, variables, context);

      const dbToolNames = new Set(remoteMcpAction?.allowed_tools.map((tool) => tool.name));
      const responseToolNames = new Set(data.allowed_tools.map((tool) => tool.name));

      if (!isSubset(dbToolNames, responseToolNames)) toast.info(t('toast.lack_of_tool_detected'));
      if (!isSubset(responseToolNames, dbToolNames)) toast.info(t('toast.new_tools_detected'));
    },
  });

  const handleSubmit = async (data: ConnectRemoteMcpForm) => {
    let toolNames;
    try {
      const { tool_names } = await connectRemoteMcpMutation.mutateAsync(data);
      toolNames = tool_names;
    } catch {
      return;
    }

    let responseRemoteMcpAction;
    if (!remoteMcpAction) {
      try {
        responseRemoteMcpAction = await createRemoteMcpMutation.mutateAsync({
          ...data,
          allowed_tools: getDefaultTools(toolNames),
        });
      } catch {
        return;
      }
    } else {
      try {
        responseRemoteMcpAction = await patchRemoteMcpMutation.mutateAsync({
          id: remoteMcpAction.id,
          allowed_tools: updateToolNamesOnReconnect(toolNames, remoteMcpAction),
        });
      } catch {
        return;
      }
    }
    setParams({ remoteMcpAction: responseRemoteMcpAction });
  };

  return {
    form,
    handleSubmit,
    isPending:
      connectRemoteMcpMutation.isPending ||
      createRemoteMcpMutation.isPending ||
      patchRemoteMcpMutation.isPending,
  };
};
